import { button, type Variant } from '../ui/kit';

/**
 * The boot screen (index.html #boot): the logo, one line of status, and — when something went
 * wrong — buttons for the way on instead of bare links. Fades out once the first frame is drawn.
 */
const root = () => document.getElementById('boot');
const part = (cls: string) => root()?.querySelector<HTMLElement>(cls) ?? null;

export interface BootAction { label: string; href?: string; onClick?: () => void; variant?: Variant }

/**
 * An explanation or a crash stands over every screen (a «found» left open would hide it) and takes the
 * taps (the scene underneath must not get them). Also after the start: the screen is only hidden then.
 */
const raise = () => { const el = root(); if (el) { el.hidden = false; el.style.zIndex = '30'; el.style.opacity = '1'; el.style.pointerEvents = 'auto'; } };

export const boot = {
  /** One line under the logo: «вхід…», «з'єднання з сервером…». */
  status(text: string) {
    const s = part('.boot-status'); if (s) { s.textContent = text; s.removeAttribute('data-tone'); }
    part('.boot-actions')?.replaceChildren();
  },
  /** Something went wrong: say what, and offer the ways on (the first is the main one). `calm`: still waiting (a search), not a failure. */
  offer(text: string, actions: BootAction[], calm = false) {
    raise();
    const s = part('.boot-status'); if (s) { s.textContent = text; if (calm) s.removeAttribute('data-tone'); else s.dataset.tone = 'bad'; }
    part('.boot-actions')?.replaceChildren(...actions.map((a, i) => button(a.label, {
      variant: a.variant ?? (i === 0 ? 'ember' : 'bronze'), block: true,
      onClick: () => { a.onClick?.(); if (a.href) location.assign(a.href); },
    })));
  },
  /** A crash: the message stays readable for a bug report. */
  fail(text: string) {
    raise();
    const s = part('.boot-status'); if (s) { s.textContent = text; s.dataset.tone = 'error'; }
  },
  /** The first frame is drawn (or an explanation was dismissed): fade out, stay in the page for later. */
  hide() {
    const el = root(); if (!el) return;
    el.style.opacity = '0'; el.style.pointerEvents = 'none';
    setTimeout(() => { if (el.style.opacity === '0') el.hidden = true; }, 400);
  },
};
